import { Visibility } from "@mui/icons-material";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as MailIcon } from "../../assets/images/MailIcon.svg";
import { useGetAllCustomers } from "../../hooks/useCustomer";
import {
  useAssetUnMappingMutation,
  useCloseTheDesk,
  useDeskCustomerUpdate,
  useDocAssetMappingMutation,
  useGetAssetList,
  useGetByEmailListId,
  useGetDocumentView,
  useRejectionMutation,
  useUpdateDocumentNameMutation,
} from "../../hooks/useDesk";
import BreadCrumb from "../../utils/BreadCrumb";
import { routerPath } from "../../utils/constants";
import {
  getMonthAndYear,
  isReadOnly,
  removeEmptyImageTags,
} from "../../utils/helpers";
import AppAutoComplete from "../AppComponents/AppAutoComplete";
import AppBackdop from "../AppComponents/AppBackdop";
import AppDialog from "../AppComponents/AppDialog";
import DocFilePreview from "../AppComponents/FileViewer";
import AppCommonPopup from "../common/AppCommonPopup";
import { AppButton, AppChip, AppIcon } from "../StyledComponent";
import CustomerInfoBar from "./components/CustomerInfoBar";
import DocumentItem from "./components/DocumentItem";
import { filterDocumentsByAsset, truncateText } from "./utils";

// =====================
// Breadcrumb Data
// =====================
const breadCrumbData = [
  { title: "Customer document details", navigate: "/deskManagement" },
  { title: "Pre processing", active: "active" },
];

export default function AssetMapping() {
  // =====================
  // State Declarations
  // =====================
  // All React state hooks for UI, data, and dialog management
  const { id } = useParams();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({
    id: null,
    familyName: "",
    status: "Active",
    inActiveClient: false,
    isCompletedTask: false,
    taskStatus: "Inprogress",
    timelineTaskId: null,
    assets: [],
  });
  const [expanded, setExpanded] = useState(null);
  const [selectedSubDocs, setSelectedSubDocs] = useState([]);
  const [draggedSubDocs, setDraggedSubDocs] = useState([]);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveDialogDocs, setMoveDialogDocs] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState("all");
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedDocToDelete, setSelectedDocToDelete] = useState(null);
  const [sendComments, setSendComments] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    confirmLabel: "",
    message: "",
    isRejected: "",
    title: "",
    action: null,
  });
  const [{ open, dataIdx }, setIsEmailDialog] = useState({
    open: false,
    dataIdx: null,
  });
  const [currentDocId, setCurrentDocId] = useState(null);
  const [editingDocId, setEditingDocId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingExt, setEditingExt] = useState("");


  // =====================
  // Data Fetching & Derived Data
  // =====================
  // Fetch desk/email data and related derived values

  const { data: docView, isLoading: isDocViewLoading } =
    useGetDocumentView(currentDocId);

  const updateMappedAssetDocuments = (assetData, data = emailData) => {
    const filtered = filterDocumentsByAsset(selectedAsset, data, assetData);
    setFilteredDocuments(filtered);
  };

  const { mutate: docAssetsMapping, isLoading: isMapping } =
    useDocAssetMappingMutation({
      onSuccess: () => {
        assetMapHandler();
        if (selectedDocToDelete) {
          setSelectedDocToDelete(null);
        }
      },
    });

  const assetMapHandler = () => {
    refetch();
    if (!selectedAsset) {
      setSelectedAsset(assetListData[0]);
    }
    handleDialogClose();
  };

  const { mutate: assetUnMap, isLoading: isAssetUnMapping } =
    useAssetUnMappingMutation(assetMapHandler);

  const responseHandler = (data) => {
    if (data?.emailProcessing?.[0]?.customerId) {
      setCustomerId(data.emailProcessing[0].customerId);
      let customer = data.emailProcessing[0]?.customer;
      setCustomerDetails(
        {
          id: customer?.id,
          familyName: customer?.familyName,
          status: customer?.clientStatus?.name,
          readOnly: isReadOnly(customer?.clientStatus?.type),
        } || {}
      );
    }
    updateMappedAssetDocuments(assetListData, data);
  };
  const { data: emailData, refetch } = useGetByEmailListId(id, responseHandler);

  const { mutate: rejection, isLoading: isRejectionLoading } =
    useRejectionMutation({
      onSuccess: () => {
        refetch();
      },
    });

  const { mutate: updateDocumentName, isLoading: isUpdatingDocumentName } =
    useUpdateDocumentNameMutation({
      onSuccess: () => {
        refetch();
        setEditingDocId(null);
        setEditingName("");
      },
    });

  const isClasiified = emailData?.isClassified || false;
  const isSingleClient = emailData?.isSingleClient || false;
  const isCustMapRequired = !isClasiified || !isSingleClient;
  const emailCustomerDetails = emailData?.emailProcessing?.[0];
  //month & year function with helpers file
  const { month, year } = getMonthAndYear(
    emailData?.emailProcessing?.[0]?.receivedAt
  );
  //customers family list
  const { data: customerFamilyList, isLoading: customerFamilyListLoading } =
    useGetAllCustomers(
      emailCustomerDetails?.customerEmail
        ? {
            email: !isClasiified ? "" : emailCustomerDetails?.customerEmail,
          }
        : null
    );
  //asset list
  const { data: assetListData, isLoading: assetListLoading } = useGetAssetList(
    customerId,
    { month, year },
    updateMappedAssetDocuments
  );

  const customerTaskTimelineId = assetListData?.length
    ? assetListData?.[0]?.taskTimelineId
    : null;
  const customerTask = assetListData?.length ? assetListData?.[0] : null;
  const deskClose = emailData?.status == "Close";
  const readOnly =
    !customerId ||
    emailData?.status == "Close" ||
    !customerTask ||
    customerTask?.readOnly ||
    customerDetails?.readOnly ||
    isCustMapRequired;
  const invalidCustomer =
    !customerId ||
    emailData?.status == "Close" ||
    !customerTask ||
    customerTask?.readOnly ||
    customerDetails?.readOnly;
  //Accordion email list shown
  const processedEmails =
    emailData?.emailProcessing?.map((email, index) => ({
      id: email?.id,
      name: email.subject,
      body: email.body,
      customerEmail: email.customerEmail,
      from: email.fromEmail,
      date: new Date(email.receivedAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      time: new Date(email.receivedAt).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      count: email.emailDocuments?.length || 0,
      isRejected: email.isRejected,
      type: "email",
      children:
        email.emailDocuments?.map((doc, docIndex) => ({
          id: doc.id,
          isMapped: !!doc?.assetIds?.length,
          documentName: doc.documentName,
          documentUrl: doc.documentUrl,
          documentSize: doc.documentSize,
          type: "document",
          isRejected: doc.isRejected,
        })) || [],
    })) || [];

  // =====================
  // Handler & Helper Functions
  // =====================

  // Handles closing the desk and opening the confirm modal
  const handleCloseDesk = (isOpen = true) => {
    setConfirmModal({
      open: isOpen,
      message:
        "Are you sure you want to close this ticket? You won't be able to make any updates after closing.",
      confirmLabel: "Yes, Close",
      action: "CLOSE_DESK",
    });
  };

  // Handles updating the desk (refetches data)
  const deskUpdateHandler = (closeDesk = true) => {
    if (closeDesk) {
      handleCloseDesk(false);
    }
    refetch();
  };

  // Handles mapping/unmapping API calls for closing the desk
  const { mutate: closeTheDesk, isPending } = useCloseTheDesk(
    id,
    deskUpdateHandler
  );
  const { mutate: updateDeskCustomer, isLoading: isUpdateCustomerLoading } =
    useDeskCustomerUpdate(id, deskUpdateHandler);

  // Handles file drop for drag-and-drop upload
  const onDropFiles = useCallback((acceptedFiles) => {
    setUploadedFiles(acceptedFiles);
    setShowMoveDialog(true);
    setMoveDialogDocs([]);
  }, []);

  // Handles drag and drop zone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropFiles,
    noClick: true,
    noKeyboard: true,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
  });

  // Handles expanding/collapsing accordions
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  // Handles selecting/deselecting a sub-document
  const handleSelectSubDoc = (subDocId) => {
    setSelectedSubDocs((prev) =>
      prev.includes(subDocId)
        ? prev.filter((id) => id !== subDocId)
        : [...prev, subDocId]
    );
  };

  // Handles drag start for sub-documents
  const handleDragStartSubDoc = (e, subDocId) => {
    setSelectedSubDocs((prev) => {
      if (!prev.includes(subDocId)) {
        return [...prev, subDocId];
      }
      return prev;
    });
    const dragIds = selectedSubDocs.length ? selectedSubDocs : [subDocId];
    setDraggedSubDocs(dragIds);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", dragIds.join(","));
    // Custom drag image with count
    const dragIcon = document.createElement("div");
    dragIcon.style.position = "absolute";
    dragIcon.style.top = "-9999px";
    dragIcon.style.left = "-9999px";
    dragIcon.style.padding = "8px 16px";
    dragIcon.style.background = "#4B3B76";
    dragIcon.style.color = "#fff";
    dragIcon.style.fontWeight = "bold";
    dragIcon.style.borderRadius = "16px";
    dragIcon.style.fontSize = "14px";
    dragIcon.innerText = `Move ${dragIds.length} documents`;
    document.body.appendChild(dragIcon);
    e.dataTransfer.setDragImage(dragIcon, 0, 0);
    setTimeout(() => {
      document.body.removeChild(dragIcon);
    }, 0);
  };

  // Handles dropping a document
  const handleDropDoc = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedSubDocs.length > 0) {
      setMoveDialogDocs(draggedSubDocs);
      setShowMoveDialog(true);
      setDraggedSubDocs([]);
    }
  };

  // Handles drag over event for document drop
  const handleDragOverDoc = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handles asset checkbox toggle
  const handleAssetCheck = (asset, id) => {
    setSelectedAssets((prev) => {
      const assetObj = { asset, id };
      return prev.some((a) => a.id === id)
        ? prev.filter((a) => a.id !== id)
        : [...prev, assetObj];
    });
  };

  // Handles dialog close and resets related state
  const handleDialogClose = () => {
    setShowMoveDialog(false);
    setSelectedAssets([]);
    setUploadedFiles([]);
    setMoveDialogDocs([]);
    setSelectedSubDocs([]);
  };

  // Handles dialog submit for mapping
  const handleDialogSubmit = async () => {
    const selectedDocs = getSelectedSubDocs();
    const docIds = selectedDocs;
    const assetIds = selectedAssets.map((asset) => asset.id.toString());
    const customerId = emailCustomerDetails?.customerId;
    if (assetIds.length > 1) {
      setConfirmModal({
        open: true,
        message: `This ${
          selectedDocs.length > 1 ? "documents are" : "document is"
        } linked to multiple assets. Do you still want to proceed?`,
        confirmLabel: "Yes, proceed",
        action: "MULTI_ASSET_MAPPING",
      });
    } else {
      const payload = {
        documents: docIds,
        assetIds: assetIds,
        customerId: customerId,
        deskManagementId: id,
      };
      docAssetsMapping(payload);
    }
  };

  // Handles asset dropdown change
  const handleAssetChange = (e) => {
    const { name, value } = e.target;
    setSelectedAsset(value);
    if (value) {
      const filtered = filterDocumentsByAsset(value, emailData, assetListData);
      setFilteredDocuments(filtered);
    } else {
      setFilteredDocuments([]);
    }
  };

  // Handles trash icon click for unmapping
  const handleTrashClick = (doc, assetId) => {
    setSelectedDocToDelete({ ...doc, assetId });
    setConfirmModal({
      open: true,
      message: `Are you sure you want to UnMapping this doc "${doc.documentName}"?`,
      confirmLabel: "Unmap",
      action: "UNMAP_DOCUMENT",
    });
  };

  // Handles unmapping a document from an asset
  const handleUnMapDocument = () => {
    if (!selectedDocToDelete) {
      handleDeleteCancel();
      return;
    }

    // Get the actual asset ID from the document's mapping
    const docIdToUnmap = selectedDocToDelete.id;
    const assetIdToRemoveFrom = selectedDocToDelete.assetId; // Get the first asset ID the document is mapped to

    if (!assetIdToRemoveFrom) {
      handleDeleteCancel();
      return;
    }

    const payload = {
      docId: docIdToUnmap,
      assetId: assetIdToRemoveFrom,
      deskManagementId: id,
    };
    assetUnMap(payload);

    handleDeleteCancel();
  };

  // Handles canceling delete dialog
  const handleDeleteCancel = () => {
    setShowDeletePopup(false);
    setSelectedDocToDelete(null);
  };

  // Handles viewing a document blob
  const handleBlobUrlConvert = async (subDoc) => {
    setCurrentDocId(subDoc.id);
  };

  // Handles opening the email dialog
  const handleEmailDialog = (e, idx) => {
    e.stopPropagation();
    setIsEmailDialog({
      open: idx !== null,
      dataIdx: idx,
    });
  };

  // Handles navigation to the task tracker
  const handleViewTask = (e) => {
    e.stopPropagation();
    navigate(
      `${routerPath.taskTracker}?timelineId=${customerTaskTimelineId}&deskId=${id}&status=breadcrumb`
    );
  };

  // Handles changing the customer family
  const handleCustomerFamily = (e) => {
    const { value } = e.target;
    setCustomerId(value);
    if (value) {
      const customerData = customerFamilyList?.find(
        (customer) => customer?.id === value
      );
      setCustomerDetails(customerData);
    }
  };

  // Handles updating the desk family
  const handleUpdateDeskFamily = () => {
    updateDeskCustomer({ customerId });
  };

  // Handles confirm modal actions
  const handleConfirmAction = () => {
    switch (confirmModal.action) {
      case "MULTI_ASSET_MAPPING":
        const selectedDocs = getSelectedSubDocs();
        const docIds = selectedDocs;
        const assetIds = selectedAssets.map((asset) => asset.id.toString());
        const customerId = emailCustomerDetails?.customerId;
        const payload = {
          documents: docIds,
          assetIds: assetIds,
          customerId: customerId,
          deskManagementId: id,
        };
        docAssetsMapping(payload);
        setConfirmModal((prev) => ({ ...prev, open: false }));
        break;
      case "CLOSE_DESK":
        closeTheDesk();
        break;
      case "UNMAP_DOCUMENT":
        handleUnMapDocument();
        setConfirmModal((prev) => ({ ...prev, open: false }));
        break;
      case "REJECTED":
        handleRejectionSubmit();
        break;
      default:
        break;
    }
  };

  // Helper: Get selected sub-documents for mapping dialog
  const getSelectedSubDocs = () => {
    let all = [];
    processedEmails.forEach((doc) => {
      if (doc.children && doc.children.length) {
        all = all.concat(
          doc.children.filter((sub) => moveDialogDocs.includes(sub.id))
        );
      }
    });
    return all;
  };

  const handleDelete = (e) => {
    setSelectedDocToDelete(e);
    setConfirmModal({
      open: true,
      message: `Do you want to reject the ${
        e.type === "email" ? "email" : "document"
      } processing?`,
      confirmLabel: "SEND",
      isRejected: true,
      title: `${e.type === "email" ? "Email" : "Document"} Rejection`,
      action: "REJECTED",
    });
  };

  const handleSendComments = (e) => {
    setSendComments(e.target.value);
  };

  const handleRejectionSubmit = () => {
    const payload = {
      rejectionId: selectedDocToDelete?.id,
      type:
        selectedDocToDelete?.type === "email" ? "emailProcessing" : "document",
      fromEmail: emailCustomerDetails?.fromEmail,
      remarks: sendComments.trim(),
    };
    rejection(payload);
    setConfirmModal((prev) => ({ ...prev, open: false }));
    setSendComments("");
  };
  const handleEditClick = (subDoc) => {
    setEditingDocId(subDoc.id);

    const lastDot = subDoc.documentName.lastIndexOf(".");
    const nameWithoutExt =
      lastDot !== -1
        ? subDoc.documentName.substring(0, lastDot)
        : subDoc.documentName;

    const ext = lastDot !== -1 ? subDoc.documentName.substring(lastDot) : "";

    setEditingName(nameWithoutExt);
    setEditingExt(ext);
  };

  const handleCancelEdit = () => {
    setEditingDocId(null);
    setEditingName("");
    setEditingExt("")
  };

    const handleSaveEdit = (subDoc) => {
      if (!editingName.trim()) {
        return;
      }

      const payload = {
        documentId: subDoc.id,
        newName: editingName.trim() + editingExt,
      };

      updateDocumentName(payload);
    };

  // =====================
  // Render
  // =====================
  return (
    <Box sx={{ background: "#FFF9F380" }}>
      <BreadCrumb breadCrumbData={breadCrumbData} />
      <Stack p={2} gap={2}>
        {/* Customer Info Bar */}
        <CustomerInfoBar
          customerId={customerId}
          customerDetails={customerDetails}
          isClasiified={isClasiified}
          emailCustomerDetails={emailCustomerDetails}
          month={month}
          year={year}
          emailData={emailData}
          customerTask={customerTask}
        />

        {/* Main Content */}
        <Stack rowGap={5}>
          <Grid container spacing={2}>
            {/* Left: List of customer documents */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{ minHeight: "100%", maxHeight: "100%", p: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1,
                  }}
                >
                  {/* Left section with background */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      background: "rgba(240, 213, 201, 1)",
                      borderRadius: 1,
                      px: 2,
                      py: 2,
                      flex: 1,
                      alignItems: "center",
                    }}
                  >
                    {/* NOTE label */}
                    <Box
                      sx={{
                        background: "#fff",
                        color: "#363157",
                        borderRadius: 1,
                        px: 2,
                        py: 0.5,
                        fontWeight: 600,
                        fontSize: 13,
                        height: "fit-content",
                      }}
                    >
                      NOTE
                    </Box>

                    {/* Instruction text */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        color: "#363157",
                        fontWeight: 500,
                        lineHeight: 1.5,
                      }}
                    >
                      First, select the asset name, and then drag and drop files
                      into the drop zone.
                    </Typography>
                  </Box>
                </Box>
                {/* Document List */}
                <Box
                  minHeight={"45dvh"}
                  maxHeight={"56dvh"}
                  overflow={"auto"}
                  pr={1}
                >
                  {processedEmails?.map((doc, idx) => (
                    <Accordion
                      key={doc.id}
                      expanded={expanded === doc.id}
                      onChange={handleAccordionChange(doc.id)}
                      sx={{
                        "&:before": {
                          display: "none",
                        },
                        mb: 1,
                        border: `${doc.isRejected ? "2px" : "1px"} solid ${
                          doc.isRejected ? "#FF8888" : "#ECEBEB"
                        }`,
                        borderRadius: 2,
                        opacity: doc?.children?.length ? 1 : 0.8,
                        backgroundColor: doc.isRejected ? "#FFF6F6" : "#fff",
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            columnGap: 2,
                            padding: "0.2rem",
                            width: "100%",
                          }}
                        >
                          {/* Left Side: Avatar + Text */}
                          <Stack
                            flexDirection={"row"}
                            alignItems={"center"}
                            justifyContent={"start"}
                            gap={1}
                            flex={1}
                          >
                            <Avatar
                              sx={{
                                width: 35,
                                height: 35,
                                fontSize: 16,
                                fontWeight: 700,
                              }}
                            >
                              {doc.count}
                            </Avatar>
                            <Stack
                              flexDirection={"row"}
                              justifyContent={"space-between"}
                              alignItems={"center"}
                              width={"100%"}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  ":hover": {
                                    textDecoration: "underline",
                                  },
                                }}
                                onClick={(e) => handleEmailDialog(e, idx)}
                              >
                                {truncateText(doc.name)}
                              </Typography>
                              <AppIcon
                                onClick={(e) => handleEmailDialog(e, idx)}
                              >
                                <MailIcon />
                              </AppIcon>
                            </Stack>
                          </Stack>

                          {/* Right Side: Mail Icon + Date + Time */}
                          <Stack alignItems={"start"}>
                            <Typography variant="caption">
                              {doc.date}
                            </Typography>
                            <Typography variant="caption">
                              {doc.time}
                            </Typography>
                          </Stack>
                          <AppIcon
                            onClick={() => handleDelete(doc)}
                            disabled={
                              doc.isRejected ||
                              (doc.children &&
                                doc.children.some((child) => child.isMapped))
                            }
                            sx={{
                              opacity:
                                doc.isRejected ||
                                (doc.children &&
                                  doc.children.some((child) => child.isMapped))
                                  ? 0.4
                                  : 1,
                              pointerEvents:
                                doc.isRejected ||
                                (doc.children &&
                                  doc.children.some((child) => child.isMapped))
                                  ? "none"
                                  : "auto",
                            }}
                          >
                            <BlockIcon
                              fontSize="small"
                              sx={{
                                "&:hover": {
                                  color: "red",
                                },
                              }}
                            />
                          </AppIcon>
                          <Divider orientation="vertical" flexItem />
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{ background: "#F7F6FB", textAlign: "center" }}
                      >
                        {doc.children?.length > 0 ? (
                          doc.children?.map((subDoc, index) => (
                            <Paper
                              key={subDoc.id}
                              elevation={0}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 1,
                                borderRadius: 1,
                                border: `${
                                  subDoc.isRejected ? "2px" : "1px"
                                }  solid ${
                                  subDoc.isRejected ? "#FF8888" : "#ECEBEB"
                                }`,
                                background: selectedSubDocs.includes(subDoc.id)
                                  ? "#E6E6F7"
                                  : "#fff",
                                gap: 1,
                                justifyContent: "space-between",
                                opacity: subDoc.isRejected ? 0.5 : 1,
                                mb: index !== doc.children.length - 1 ? 1 : 0,
                                backgroundColor: subDoc.isRejected
                                  ? "#FFF6F6"
                                  : "#fff",
                              }}
                              draggable={!readOnly && !subDoc.isMapped}
                              onDragStart={(e) =>
                                handleDragStartSubDoc(e, subDoc.id)
                              }
                            >
                              <Checkbox
                                checked={
                                  selectedSubDocs.includes(subDoc.id) ||
                                  subDoc?.isMapped
                                }
                                onChange={() => handleSelectSubDoc(subDoc.id)}
                                disabled={subDoc?.isMapped || readOnly}
                                sx={{
                                  p: 0,
                                  mr: 1,
                                  color: "#BE8976",
                                  "&.Mui-checked": {
                                    color: "#BE8976",
                                  },
                                  opacity: subDoc?.isMapped ? 0.5 : null,
                                  pointerEvents: subDoc.isRejected
                                    ? "none"
                                    : "auto",
                                }}
                              />

                              <Stack
                                flexDirection={"row"}
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                flex={1}
                                gap={1}
                                sx={{ cursor: readOnly ? "default" : "grab" }}
                              >
                                <Stack
                                  flexDirection={"row"}
                                  gap={1}
                                  alignItems={"center"}
                                >
                                  <Box
                                    border={"1px solid #ccc"}
                                    alignItems={"center"}
                                    justifyContent={"center"}
                                    display={"flex"}
                                    p={"5px"}
                                    borderRadius={"5px"}
                                  >
                                    <DescriptionRoundedIcon fontSize="small" />
                                  </Box>
                                  {/* <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: "200px",
                                    }}
                                  >
                                    {subDoc.documentName}
                                  </Typography> */}
                                  {subDoc.id === editingDocId ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <TextField
                                        size="small"
                                        value={editingName}
                                        onChange={(e) =>
                                          setEditingName(e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            handleSaveEdit(subDoc);
                                          } else if (e.key === "Escape") {
                                            handleCancelEdit();
                                          }
                                        }}
                                        autoFocus
                                        sx={{ minWidth: '200px' }}
                                        InputProps={{
                                          endAdornment: (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleSaveEdit(subDoc)}
                                                disabled={isUpdatingDocumentName}
                                                sx={{ 
                                                  color: 'green',
                                                  p: 0.5,
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(0, 255, 0, 0.1)'
                                                  }
                                                }}
                                              >
                                                <CheckIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                onClick={handleCancelEdit}
                                                sx={{ 
                                                  color: 'red',
                                                  p: 0.5,
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(255, 0, 0, 0.1)'
                                                  }
                                                }}
                                              >
                                                <CloseIcon sx={{ fontSize: 16 }} />
                                              </IconButton>
                                            </Box>
                                          ),
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxWidth: "200px",
                                      }}
                                    >
                                      {subDoc.documentName}
                                    </Typography>
                                  )}
                                  {subDoc.id !== editingDocId && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditClick(subDoc)}
                                      disabled={isUpdatingDocumentName}
                                      sx={{
                                        pointerEvents:
                                          subDoc.isRejected || subDoc.isMapped
                                            ? "none"
                                            : "auto",
                                        opacity:
                                          subDoc.isRejected || subDoc.isMapped
                                            ? 0.5
                                            : 1,
                                      }}
                                    >
                                      <EditIcon
                                        fontSize="small"
                                        sx={{ color: "#BE8976" }}
                                      />
                                    </IconButton>
                                  )}
                                </Stack>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {subDoc.documentSize}
                                </Typography>
                              </Stack>
                              <Divider orientation="vertical" flexItem />
                              <AppIcon
                                onClick={() => handleBlobUrlConvert(subDoc)}
                                sx={{pointerEvents:
                                    subDoc.isRejected || subDoc.isMapped
                                      ? "none"
                                      : "auto",
                                  opacity:
                                    subDoc.isRejected || subDoc.isMapped
                                      ? 0.5
                                      : 1,}}
                              >
                                <Visibility fontSize="small" />
                              </AppIcon>
                              <Divider orientation="vertical" flexItem />
                              <AppIcon
                                onClick={() => handleDelete(subDoc)}
                                sx={{
                                  pointerEvents:
                                    subDoc.isRejected || subDoc.isMapped
                                      ? "none"
                                      : "auto",
                                  opacity:
                                    subDoc.isRejected || subDoc.isMapped
                                      ? 0.5
                                      : 1,
                                }}
                              >
                                <BlockIcon
                                  fontSize="small"
                                  sx={{
                                    "&:hover": {
                                      color: "red",
                                    },
                                  }}
                                />
                              </AppIcon>
                            </Paper>
                          ))
                        ) : (
                          <Typography>No attachments</Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Right: List of customer assets and upload */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{ minHeight: "100%", maxHeight: "100%", p: 1 }}
              >
                <Grid container spacing={2} alignItems="center" mb={4}>
                  <Grid item xs={12} md={6} lg={isCustMapRequired ? 8 : 6}>
                    <AppAutoComplete
                      fullWidth
                      options={customerFamilyList ?? []}
                      value={customerId}
                      loading={customerFamilyListLoading}
                      disabled={isSingleClient || customerFamilyListLoading}
                      onChange={handleCustomerFamily}
                      label={"Family Name"}
                      labelParam="familyName"
                      valueParam="id"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={isCustMapRequired ? 4 : 6}>
                    {!isCustMapRequired ? (
                      <AppAutoComplete
                        fullWidth
                        disableClearable
                        options={[
                          { id: "all", asset: "All Assets" },
                          ...(assetListData || []),
                        ]}
                        loading={assetListLoading}
                        disabled={assetListLoading || !customerId}
                        value={selectedAsset}
                        onChange={handleAssetChange}
                        labelParam="asset"
                        valueParam={"id"}
                        label={"Asset List"}
                      />
                    ) : (
                      <AppButton
                        fullWidth
                        variant="contained"
                        disabled={invalidCustomer}
                        onClick={handleUpdateDeskFamily}
                      >
                        Update Family
                      </AppButton>
                    )}
                  </Grid>
                </Grid>

                {/* Drag and Drop Upload */}
                <Box
                  onClick={() => {
                    if (selectedSubDocs.length > 0) {
                      setShowMoveDialog(true);
                      setUploadedFiles([]);
                      setMoveDialogDocs(selectedSubDocs);
                    }
                  }}
                  onDrop={handleDropDoc}
                  onDragOver={handleDragOverDoc}
                  sx={{
                    border: "1px dashed #BE8976",
                    borderRadius: 2,
                    // p: 1,
                    textAlign: "center",
                    background: "#F7F6FB",
                    mb: 2,
                    minHeight: 120,
                    position: "relative",
                    "&:hover": {
                      background: "rgba(190, 137, 118, 0.1)",
                    },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CloudUploadIcon
                    sx={{
                      color: "#BE8976",
                      fontSize: "2rem",
                    }}
                  />
                  <Stack gap={0.5}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#BE8976",
                        fontWeight: 500,
                        fontSize: "14px",
                        textDecoration: "underline",
                        cursor: selectedSubDocs.length > 0 ? "pointer" : null,
                        opacity: selectedSubDocs.length > 0 ? 1 : 0.5,
                      }}
                    >
                      Select File to Upload
                    </Typography>
                    <Typography variant="caption">or</Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#BE8976",
                        fontWeight: 500,
                        fontSize: "14px",
                        textDecoration: "underline",
                      }}
                    >
                      Drag and drop a document or file here
                    </Typography>
                  </Stack>
                </Box>

                {/* Asset Data List */}
                <Box p={1} height={"36dvh"}>
                  <Typography variant="body1" mb={2}>
                    Asset Data List
                  </Typography>

                  <Stack
                    overflow={"auto"}
                    minHeight={"35dvh"}
                    maxHeight={"30dvh"}
                    pr={2}
                    rowGap={3}
                  >
                    {customerId && !!filteredDocuments?.length ? (
                      filteredDocuments?.map((asset, index) => (
                        <Grid container spacing={2} key={index} xs={12}>
                          {selectedAsset == "all" && (
                            <Grid item xs={12}>
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 1,
                                  background: "#F7F6FB",
                                  borderRadius: 1,
                                  border: "1px solid #D9D4FE",
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{ color: "#363157" }}
                                >
                                  {asset.asset}
                                </Typography>
                              </Paper>
                            </Grid>
                          )}
                          <Grid item container spacing={1} xs={12}>
                            {!!asset.documents?.length ? (
                              asset.documents.map((doc, docIndex) => (
                                <DocumentItem
                                  key={docIndex}
                                  assetId={asset?.assetId}
                                  document={doc}
                                  readOnly={readOnly}
                                  handleTrashClick={handleTrashClick}
                                />
                              ))
                            ) : (
                              <Grid item xs={12} textAlign={"center"}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {selectedAsset
                                    ? "No documents found for this asset"
                                    : "No asset data right now"}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                          {index !== filteredDocuments.length - 1 && (
                            <Grid item xs={12}>
                              <Divider sx={{ my: 2 }} />
                            </Grid>
                          )}
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12} textAlign={"center"}>
                        <Typography variant="body2" color="text.secondary">
                          {selectedAsset
                            ? "No documents found for selected asset"
                            : "No asset data right now"}
                        </Typography>
                      </Grid>
                    )}
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          {/* Final Action  */}
          {
            <Stack
              direction="row"
              justifyContent="flex-end"
              textAlign="right"
              gap={2}
            >
              {!!customerId &&
                customerTask?.timelineStatus &&
                (readOnly || deskClose ? (
                  <AppButton
                    variant="contained"
                    disabled={isCustMapRequired}
                    onClick={handleViewTask}
                  >
                    View Task Tracker
                  </AppButton>
                ) : (
                  <AppButton
                    variant="contained"
                    disabled={isCustMapRequired || readOnly}
                    onClick={handleCloseDesk}
                  >
                    SAVE
                  </AppButton>
                ))}
            </Stack>
          }
        </Stack>
      </Stack>

      {/* Document Asset mapping Dialog */}
      {selectedSubDocs.length > 0 && moveDialogDocs.length > 0 && (
        <Dialog
          open={showMoveDialog}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2, p: 0 },
          }}
        >
          {/* Common Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            pt={3}
          >
            <Typography variant="h4" sx={{ color: "#363157" }}>
              Select Asset List
            </Typography>
            {!isMapping && (
              <IconButton
                onClick={handleDialogClose}
                sx={{
                  borderRadius: 0,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>

          <DialogContent sx={{ pt: 1 }}>
            {/* Common Customer Name Section */}
            <Box mb={2}>
              <Typography fontSize={14} fontWeight={500} mb={0.5}>
                Customer / Family Name
              </Typography>
              <Box
                sx={{
                  background: "#F6F6F6",
                  borderRadius: 1,
                  border: "1px solid #B9B9B9",
                  px: 2,
                  py: 1,
                  color: "#363157",
                  fontWeight: 500,
                  fontSize: 16,
                }}
              >
                {emailCustomerDetails?.customer?.familyName}
              </Box>
            </Box>

            {/* Conditional Content Section */}
            {
              // Show selected documents
              moveDialogDocs.length > 0 && (
                <Box mb={2}>
                  <Typography fontSize={15} mb={1}>
                    Customer Documents
                  </Typography>
                  {getSelectedSubDocs().map((subDoc) => (
                    <Box
                      key={subDoc.id}
                      sx={{
                        border: "1px solid #B9B9B9",
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        mb: 1,
                        background: "#fff",
                      }}
                    >
                      <InsertDriveFileIcon sx={{ color: "#363157", mr: 1 }} />
                      <Typography flex={1} fontSize={15}>
                        {subDoc?.documentName}
                      </Typography>
                      <Typography fontSize={13} color="#A6A5A5">
                        {subDoc?.documentSize}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )
            }

            {/* Common Asset List Section */}
            <Box>
              <Typography fontSize={15} mb={1}>
                Asset List
              </Typography>
              {assetListData?.map((item) => (
                <Box
                  key={item.id}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <Checkbox
                    checked={selectedAssets.some((a) => a.id === item.id)}
                    onChange={() => handleAssetCheck(item.asset, item.id)}
                    sx={{
                      color: "#BE8976",
                      "&.Mui-checked": {
                        color: "#BE8976",
                      },
                    }}
                  />
                  <Typography fontSize={15}>{item.asset}</Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>

          {/* Common Submit Button */}
          <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
            <Button
              onClick={handleDialogSubmit}
              variant="contained"
              fullWidth
              sx={{
                bgcolor: "#BE8976",
                color: "#fff",
                borderRadius: 2,
                fontWeight: 600,
                fontSize: 16,
                height: 48,
                "&:hover": { bgcolor: "#BE8976" },
              }}
              disabled={selectedAssets.length === 0 || isMapping}
            >
              {isMapping ? "Submitting..." : "SUBMIT"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* file viewer */}
      {currentDocId && (
        <DocFilePreview
          isLoading={isDocViewLoading}
          isModalOpen={currentDocId}
          selectedFile={docView}
          setIsModalOpen={() => setCurrentDocId(null)}
        />
      )}

      <AppDialog
        title={processedEmails[dataIdx]?.name}
        open={open}
        onClose={(e) => handleEmailDialog(e, null)}
      >
        <Stack gap={2}>
          <Typography>
            From :{" "}
            <span style={{ fontWeight: "300" }}>
              {processedEmails[dataIdx]?.customerEmail}
            </span>
          </Typography>
          <Stack
            border={"0.7px solid #D8D8D8"}
            borderRadius={"0.5rem"}
            minHeight={"4rem"}
            maxHeight={"20rem"}
            p={"1rem"}
            overflow={"auto"}
          >
            <div
              style={{
                fontFamily: "Arial,Helvetica,sans-serif",
                pointerEvents: "none",
              }}
              dangerouslySetInnerHTML={{
                __html:
                  removeEmptyImageTags(processedEmails[dataIdx]?.body) || "",
              }}
            />
          </Stack>
          <Typography>
            Date & Time :{"  "}
            <span
              style={{
                fontWeight: "300",
              }}
            >
              {processedEmails[dataIdx]?.date}

              <span style={{ color: "rgba(0, 0, 0, 0.4)", margin: 6 }}>|</span>
              {processedEmails[dataIdx]?.time}
            </span>
          </Typography>
          <AppChip
            label={`No. of Attachments - ${processedEmails[dataIdx]?.count}`}
          />
        </Stack>
      </AppDialog>

      <AppCommonPopup
        open={confirmModal.open}
        onClose={() => setConfirmModal((prev) => ({ ...prev, open: false }))}
        onConfirm={handleConfirmAction}
        message={confirmModal.message}
        confrimLabel={confirmModal.confirmLabel}
        isRejected={confirmModal.isRejected}
        title={confirmModal.title}
        handleSendComments={handleSendComments}
        isLoading={isRejectionLoading}
      />
      <AppBackdop
        open={isMapping || isAssetUnMapping || isRejectionLoading || isUpdatingDocumentName}
        handleClose={() => {}}
      />
    </Box>
  );
}
